import React from 'react';
import {
  epochDateString,
  isDateRangeEmpty,
  isDateRangeNotEmpty,
  isDateRangeValue,
  isTopKFilter,
  operatorColors,
  operatorLabels,
  PeriodBehavior,
  SortDir,
} from '@fabric-ui/components';
import { Box } from '@mui/material';
import { add, addDays, addMonths, addWeeks, format, isEqual, subDays, subMonths, subWeeks } from 'date-fns';
import { FieldFilterModeType, isAdvancedFilterMode } from '../utils/filterUtils';
import { isJSON } from '../utils/utils';
import { ConditionOperand, FieldFilterValue, FilterOption } from './filters/types';

export const getDisplayValue = value => (isJSON(value) ? (value.from || value.to ? `${value.from || ''}-${value.to || ''}` : '') : value);
export const getField = (field: unknown): Array<unknown> | undefined => (field ? (Array.isArray(field) ? field : [field]) : undefined);

const defaultDateFormat = 'MMM dd, yyyy';

const shiftDate = (date: Date, unit: string, amount: number, op: 'add' | 'sub') => {
  if (unit === 'WEEKS') {
    return op === 'sub' ? subWeeks(date, amount) : addWeeks(date, amount);
  }
  if (unit === 'MONTHS') {
    return op === 'sub' ? subMonths(date, amount) : addMonths(date, amount);
  }
  return op === 'sub' ? subDays(date, amount) : addDays(date, amount);
};

export const formatDateValue = ({ value, dateFormat = defaultDateFormat }) => {
  if (value === undefined) {
    return '';
  }
  if (isDateRangeNotEmpty(value)) {
    return 'Is Not Empty';
  }

  if (value === null || isDateRangeEmpty(value)) {
    return 'Is Empty';
  }

  const relative = (() => {
    if (value?.relative && (value.relative.unit || value.relative.periodBehaviour)) {
      return value.relative;
    }
    if (value?.unit) {
      return value;
    }
    return null;
  })();

  if (relative) {
    const now = new Date();

    if (relative?.periodBehaviour) {
      const isRange = [PeriodBehavior.last, PeriodBehavior.previous].includes(relative.periodBehaviour);
      const start = isRange ? subDays(now, relative.value) : now;
      const end = isRange ? now : addDays(now, relative.value);
      return `${format(start, dateFormat)} - ${format(end, dateFormat)}`;
    }

    const { unit = 'DAYS', value: amount, direction } = relative;
    const isRange = direction === 'last' || direction === 'previous';
    const start = isRange ? shiftDate(now, unit, amount, 'sub') : now;
    const end = isRange ? now : shiftDate(now, unit, amount, 'add');
    return `${format(start, dateFormat)} - ${format(end, dateFormat)}`;
  }

  return isDateRangeValue(value)
    ? isEqual(new Date(value.from), new Date(epochDateString)) && value.to
      ? `Before ${format(add(new Date(value.to), { days: 1 }), dateFormat)}`
      : value.to
        ? `${format(new Date(value.from), dateFormat)} - ${format(new Date(value.to), dateFormat)}`
        : ''
    : '';
};

export const conditionTooltip = (operand: ConditionOperand, index: number, arr: ConditionOperand[], operator: string) => (
  <span key={index}>
    <span style={{ fontWeight: 600 }}>{operatorLabels[operand.operator!]} </span>
    <span>{getDisplayValue(operand.value)}</span>{' '}
    {index === arr.length - 1 ? (
      ''
    ) : (
      <Box component="span" sx={{ fontWeight: 600, color: operatorColors[operator] }}>
        {operator.toUpperCase()}{' '}
      </Box>
    )}
  </span>
);

export interface TooltipValueParams {
  filterObject: FieldFilterValue | undefined;
  mode: FieldFilterModeType;
  isDateField: boolean;
  valueOperator: string;
  value: unknown;
  operator: string;
  calcOptions?: FilterOption[];
}

export interface DisplayValueParams {
  filterObject: FieldFilterValue | undefined;
  isDateField: boolean;
  valueOperator: string;
  value: unknown;
  getValue?: (v: unknown) => unknown;
  calcOptions: FilterOption[];
  measurementsAsObject?: Record<string, { displayName?: string }>;
}

/**
 * Calculates the tooltip value for a field filter
 * @param params - Parameters for calculating tooltip value
 * @returns The tooltip string to display
 */
export const calculateTooltipValue = ({
  filterObject,
  mode,
  isDateField,
  valueOperator,
  value,
  operator,
  calcOptions,
}: TooltipValueParams): string => {
  const getOptionLabel = (v: unknown) => calcOptions?.find(o => o.value === v)?.title ?? String(v);

  if (isAdvancedFilterMode(mode) || !filterObject) {
    return Array.isArray(value) ? value.map(getOptionLabel).join(', ') : '';
  }

  if (isDateField) {
    return formatDateValue(filterObject! as { value: any; dateFormat?: string });
  }

  if (Array.isArray(filterObject[valueOperator])) {
    return (filterObject[valueOperator] as ConditionOperand[])
      .map((operand, index, arr) => {
        const tooltip = conditionTooltip(operand, index, arr, operator);
        // Convert React element to string representation for SelectTrigger
        // Extract the text content from the React element
        if (React.isValidElement(tooltip)) {
          return ''; // SelectTrigger can't display React elements, return empty
        }
        return String(tooltip);
      })
      .join('');
  }

  return Array.isArray(value) ? value.join(', ') : '';
};

/**
 * Calculates the display value for a field filter
 * @param params - Parameters for calculating display value
 * @returns The display string to show in the filter trigger
 */
export const calculateDisplayValue = ({
  filterObject,
  isDateField,
  valueOperator,
  value,
  getValue,
  calcOptions,
  measurementsAsObject,
}: DisplayValueParams): string => {
  // No filter object: show first value with optional transformation
  if (!filterObject) {
    if (!Array.isArray(value) || value.length === 0) {
      return '';
    }

    if (getValue && value[0] !== getValue(value[0])) {
      return getValue(value[0]) as string;
    }

    return calcOptions.find(o => o.value === value[0])?.title || `${value}`;
  }

  if (isDateField) {
    return formatDateValue(filterObject! as { value: any; dateFormat?: string });
  }

  if (isTopKFilter(filterObject.type!)) {
    const { dir, top, value: metric } = filterObject;
    if (!top || !metric) {
      return '';
    }
    const dirLabel = dir === SortDir.DESC ? 'Top' : 'Bottom';
    const metricDisplayName = measurementsAsObject?.[metric as string]?.displayName || metric;
    return `${dirLabel} ${top} by ${metricDisplayName}`;
  }

  const filterOperands = filterObject[valueOperator];
  if (Array.isArray(filterOperands) && filterOperands[0]) {
    const firstOperand = filterOperands[0] as ConditionOperand;
    return `${operatorLabels[firstOperand?.operator as string]}  ${getDisplayValue(firstOperand?.value)}`;
  }

  return Array.isArray(value) ? value.join(', ') : '';
};
